/**
 * SweepProgressModal Component
 *
 * Modal that handles the full sweep purchase flow:
 * 1. Confirmation stage - Review NFTs and total cost
 * 2. Purchasing stage - Sequential Xaman signing for each NFT
 * 3. Complete stage - Summary of results
 */

import React, { useEffect, useRef } from 'react';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { Progress } from '../../ui/components/Progress';
import {
  FeatherX,
  FeatherCheck,
  FeatherAlertCircle,
  FeatherShoppingCart,
  FeatherClock,
} from '@subframe/core';
import { useXamanPayloadStatus } from '../../hooks/useXamanPayloadStatus';
import type { NFT } from '../../types';
import type { SweepPurchaseState, PurchaseFailure } from '../../hooks/useSweepPurchase';
import { is3DModel } from '../../utils/media';
import type { SigningPayload } from '../../types/signing';
import { getWalletProvider } from '../../lib/wallet';
import { useWcSigningRequest } from '../../hooks/useWcSigningRequest';

function formatExpiry(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

interface SweepProgressModalProps {
  /** Whether modal is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** NFTs being swept */
  nfts: NFT[];
  /** Current purchase state */
  state: SweepPurchaseState;
  /** Totals for the sweep */
  totals: { subtotal: number; fees: number; total: number };
  /** Start the purchase process */
  onStartPurchase: () => void;
  /** Cancel confirmation */
  onCancelConfirmation: () => void;
  /** Handle successful signing */
  onSignSuccess: (txid?: string) => void;
  /** Handle rejected/expired signing */
  onSignRejected: (reason?: string) => void;
  /** Cancel remaining purchases */
  onCancelRemaining: () => void;
  /** Complete and close */
  onComplete: () => void;
}

export function SweepProgressModal({
  open,
  onClose,
  nfts,
  state,
  totals,
  onStartPurchase,
  onCancelConfirmation,
  onSignSuccess,
  onSignRejected,
  onCancelRemaining,
  onComplete,
}: SweepProgressModalProps) {
  const { stage, currentIndex, totalCount, currentPayload, currentNft, completedPurchases, failedPurchases } = state;

  // Don't render if not open
  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()} className="z-50">
      <Dialog.Content className="max-w-lg w-full p-0 mobile:max-w-full mobile:m-4 !gap-0 !items-stretch">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-solid border-neutral-border">
          <div className="flex items-center gap-2">
            <FeatherShoppingCart className="h-5 w-5 text-brand-600" />
            <h2 className="text-heading-3 font-heading-3 text-default-font">
              {stage === 'confirming' && 'Confirm Sweep Purchase'}
              {stage === 'purchasing' && 'Purchasing NFTs'}
              {stage === 'complete' && 'Sweep Complete'}
            </h2>
          </div>
          {stage !== 'purchasing' && (
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-neutral-100 transition-colors"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-4 mobile:p-3">
          {/* Confirmation Stage */}
          {stage === 'confirming' && (
            <ConfirmationContent
              nfts={nfts}
              totals={totals}
              onStartPurchase={onStartPurchase}
              onCancel={onCancelConfirmation}
            />
          )}

          {/* Purchasing Stage */}
          {stage === 'purchasing' && (
            <PurchasingContent
              currentIndex={currentIndex}
              totalCount={totalCount}
              currentNft={currentNft}
              currentPayload={currentPayload}
              completedPurchases={completedPurchases}
              failedPurchases={failedPurchases}
              onSignSuccess={onSignSuccess}
              onSignRejected={onSignRejected}
              onCancelRemaining={onCancelRemaining}
            />
          )}

          {/* Complete Stage */}
          {stage === 'complete' && (
            <CompleteContent
              completedPurchases={completedPurchases}
              failedPurchases={failedPurchases}
              onComplete={onComplete}
            />
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
}

// ============================================================================
// Confirmation Content
// ============================================================================

interface ConfirmationContentProps {
  nfts: NFT[];
  totals: { subtotal: number; fees: number; total: number };
  onStartPurchase: () => void;
  onCancel: () => void;
}

function ConfirmationContent({ nfts, totals, onStartPurchase, onCancel }: ConfirmationContentProps) {
  return (
    <div className="flex flex-col gap-4">
      {/* Info message */}
      <div className="p-3 bg-brand-50 rounded-md">
        <p className="text-body font-body text-brand-700">
          You will sign {nfts.length} transaction{nfts.length !== 1 ? 's' : ''} in Xaman to complete this purchase.
          Each NFT requires a separate signature.
        </p>
      </div>

      {/* NFT list */}
      <div className="max-h-[250px] overflow-y-auto border border-solid border-neutral-border rounded-md mobile:max-h-[200px]">
        {nfts.map((nft, index) => (
          <div
            key={nft.id}
            className={`flex items-center gap-3 p-3 ${index !== nfts.length - 1 ? 'border-b border-solid border-neutral-border' : ''}`}
          >
            {is3DModel(nft) ? (
              <div className="w-12 h-12 rounded-md flex items-center justify-center bg-neutral-100 flex-shrink-0">
                <span style={{ fontSize: '20px' }}>🧊</span>
              </div>
            ) : (
              <img
                src={nft.thumbnail_url || nft.image_url || '/placeholder-nft.png'}
                alt={nft.name || 'NFT'}
                className="w-12 h-12 rounded-md object-cover"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-body-bold font-body-bold text-default-font truncate">
                {nft.name || `NFT #${nft.nft_token_id.slice(-8)}`}
              </p>
            </div>
            <span className="text-body-bold font-body-bold text-default-font whitespace-nowrap">
              {Number(nft.min_sell_price || 0).toFixed(2)} XRP
            </span>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div className="flex flex-col gap-2 p-3 bg-neutral-50 rounded-md">
        <div className="flex justify-between">
          <span className="text-body font-body text-subtext-color">Subtotal</span>
          <span className="text-body font-body text-default-font">{totals.subtotal.toFixed(2)} XRP</span>
        </div>
        {totals.fees > 0 && (
          <div className="flex justify-between">
            <span className="text-body font-body text-subtext-color">Platform Fees (~1.6%)</span>
            <span className="text-body font-body text-default-font">~{totals.fees.toFixed(2)} XRP</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t border-solid border-neutral-border">
          <span className="text-body-bold font-body-bold text-default-font">Total</span>
          <span className="text-heading-3 font-heading-3 text-brand-600">{totals.total.toFixed(2)} XRP</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 mobile:flex-col-reverse">
        <Button
          variant="neutral-secondary"
          onClick={onCancel}
          className="flex-1 mobile:w-full"
        >
          Cancel
        </Button>
        <Button
          variant="brand-primary"
          onClick={onStartPurchase}
          className="flex-1 mobile:w-full"
        >
          Start Purchase
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Purchasing Content
// ============================================================================

interface PurchasingContentProps {
  currentIndex: number;
  totalCount: number;
  currentNft: NFT | null;
  currentPayload: SigningPayload | null;
  completedPurchases: string[];
  failedPurchases: PurchaseFailure[];
  onSignSuccess: (txid?: string) => void;
  onSignRejected: (reason?: string) => void;
  onCancelRemaining: () => void;
}

/**
 * Xaman resolver: QR + deep link, status via useXamanPayloadStatus (unchanged path).
 * Hooks can't branch on provider, so resolution lives in per-provider components.
 */
function XamanPurchaseResolver({
  currentPayload,
  onSignSuccess,
  onSignRejected,
  onCancelRemaining,
  currentIndex,
  totalCount,
}: {
  currentPayload: SigningPayload;
  onSignSuccess: (txid?: string) => void;
  onSignRejected: (reason?: string) => void;
  onCancelRemaining: () => void;
  currentIndex: number;
  totalCount: number;
}) {
  const { status, expiresInSeconds } = useXamanPayloadStatus({
    uuid: currentPayload.id || '',
    websocketUrl: currentPayload.websocketUrl,
    onSigned: (txHash) => onSignSuccess(txHash),
    onRejected: () => onSignRejected('Transaction rejected'),
    onExpired: () => onSignRejected('Transaction expired'),
  });

  const getStatusMessage = (): string => {
    switch (status) {
      case 'pending':
        return 'Scan the QR code with Xaman or tap the button below';
      case 'opened':
        return 'Opened in Xaman app - Please review and sign';
      case 'signing':
        return 'Signing transaction...';
      case 'dispatching':
        return 'Processing transaction on XRPL...';
      case 'signed':
        return 'Transaction successful!';
      case 'rejected':
        return 'Transaction was rejected';
      case 'expired':
        return 'Request expired';
      default:
        return 'Scan the QR code with Xaman or tap the button below';
    }
  };

  const isProcessing = status === 'signing' || status === 'dispatching';
  const isComplete = status === 'signed' || status === 'rejected' || status === 'expired';

  return (
    <>
      {!isProcessing && !isComplete && (
        <div className="flex flex-col items-center gap-3 p-4 border border-solid border-neutral-border rounded-md">
          <p className="text-body font-body text-subtext-color text-center">
            {getStatusMessage()}
          </p>
          {currentPayload.qrUrl && (
            <img
              src={currentPayload.qrUrl}
              alt="Xaman QR Code"
              className="w-48 h-48 rounded-md"
            />
          )}
          {currentPayload.deepLink && (
            <Button
              variant="brand-primary"
              onClick={() => window.open(currentPayload.deepLink, '_blank')}
              className="w-full"
            >
              Open in Xaman
            </Button>
          )}
          {expiresInSeconds != null && expiresInSeconds > 0 && (
            <p className="text-caption font-caption text-subtext-color">
              Expires in {formatExpiry(expiresInSeconds)}
            </p>
          )}
        </div>
      )}

      {isProcessing && (
        <div className="flex flex-col items-center gap-3 p-4 border border-solid border-neutral-border rounded-md">
          <FeatherClock className="h-8 w-8 text-brand-600 animate-spin" />
          <p className="text-body font-body text-default-font text-center">
            {getStatusMessage()}
          </p>
        </div>
      )}

      <Button
        variant="neutral-secondary"
        onClick={onCancelRemaining}
        disabled={isProcessing}
        className="w-full"
      >
        Cancel Remaining ({totalCount - currentIndex - 1} left)
      </Button>
    </>
  );
}

/**
 * Client-signing resolver (joey-wc): approve prompt (no QR), resolution via the
 * WalletConnect hook which signs over the session and verifies on-chain.
 */
function WcPurchaseResolver({
  currentPayload,
  onSignSuccess,
  onSignRejected,
  onCancelRemaining,
  currentIndex,
  totalCount,
}: {
  currentPayload: SigningPayload;
  onSignSuccess: (txid?: string) => void;
  onSignRejected: (reason?: string) => void;
  onCancelRemaining: () => void;
  currentIndex: number;
  totalCount: number;
}) {
  const { status } = useWcSigningRequest({
    payload: currentPayload,
    onSigned: (txHash) => onSignSuccess(txHash),
    onRejected: () => onSignRejected('Transaction rejected'),
    onExpired: () => onSignRejected('Transaction expired'),
  });

  const isProcessing = status === 'signing' || status === 'dispatching';

  return (
    <>
      <div className="flex flex-col items-center gap-3 p-4 border border-solid border-neutral-border rounded-md">
        <FeatherClock className="h-8 w-8 text-brand-600 animate-spin" />
        <p className="text-body font-body text-default-font text-center">
          Approve the purchase in your wallet
        </p>
      </div>

      <Button
        variant="neutral-secondary"
        onClick={onCancelRemaining}
        disabled={isProcessing}
        className="w-full"
      >
        Cancel Remaining ({totalCount - currentIndex - 1} left)
      </Button>
    </>
  );
}

function PurchasingContent({
  currentIndex,
  totalCount,
  currentNft,
  currentPayload,
  completedPurchases,
  failedPurchases,
  onSignSuccess,
  onSignRejected,
  onCancelRemaining,
}: PurchasingContentProps) {
  const progressPercent = ((currentIndex) / totalCount) * 100;
  const isClientSigning = !!currentPayload && getWalletProvider(currentPayload.provider).capabilities.clientSigning;

  return (
    <div className="flex flex-col gap-4">
      {/* Progress */}
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <span className="text-body-bold font-body-bold text-default-font">
            Purchasing {currentIndex + 1} of {totalCount}
          </span>
          <span className="text-caption font-caption text-subtext-color">
            {completedPurchases.length} done, {failedPurchases.length} failed
          </span>
        </div>
        <Progress value={progressPercent} />
      </div>

      {/* Current NFT */}
      {currentNft && (
        <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-md">
          {is3DModel(currentNft) ? (
            <div className="w-16 h-16 rounded-md flex items-center justify-center bg-neutral-100 flex-shrink-0">
              <span style={{ fontSize: '24px' }}>🧊</span>
            </div>
          ) : (
            <img
              src={currentNft.thumbnail_url || currentNft.image_url || '/placeholder-nft.png'}
              alt={currentNft.name || 'NFT'}
              className="w-16 h-16 rounded-md object-cover"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-body-bold font-body-bold text-default-font truncate">
              {currentNft.name || `NFT #${currentNft.nft_token_id.slice(-8)}`}
            </p>
            <p className="text-heading-3 font-heading-3 text-brand-600">
              {Number(currentNft.min_sell_price || 0).toFixed(2)} XRP
            </p>
          </div>
        </div>
      )}

      {/* Per-provider resolution. Keyed by payload id so a fresh resolver (and its
          resolution hook) mounts for each sequential NFT. */}
      {currentPayload && (
        isClientSigning ? (
          <WcPurchaseResolver
            key={currentPayload.id}
            currentPayload={currentPayload}
            onSignSuccess={onSignSuccess}
            onSignRejected={onSignRejected}
            onCancelRemaining={onCancelRemaining}
            currentIndex={currentIndex}
            totalCount={totalCount}
          />
        ) : (
          <XamanPurchaseResolver
            key={currentPayload.id}
            currentPayload={currentPayload}
            onSignSuccess={onSignSuccess}
            onSignRejected={onSignRejected}
            onCancelRemaining={onCancelRemaining}
            currentIndex={currentIndex}
            totalCount={totalCount}
          />
        )
      )}

      {/* Waiting for payload */}
      {!currentPayload && currentNft && (
        <>
          <div className="flex flex-col items-center gap-3 p-4">
            <div className="w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-body font-body text-subtext-color">
              Preparing transaction...
            </p>
          </div>
          <Button
            variant="neutral-secondary"
            onClick={onCancelRemaining}
            className="w-full"
          >
            Cancel Remaining ({totalCount - currentIndex - 1} left)
          </Button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Complete Content
// ============================================================================

interface CompleteContentProps {
  completedPurchases: string[];
  failedPurchases: PurchaseFailure[];
  onComplete: () => void;
}

function CompleteContent({ completedPurchases, failedPurchases, onComplete }: CompleteContentProps) {
  const hasSuccesses = completedPurchases.length > 0;
  const hasFailures = failedPurchases.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Summary */}
      <div className="flex flex-col items-center gap-3 py-4">
        {hasSuccesses && !hasFailures && (
          <>
            <div className="w-16 h-16 rounded-full bg-success-50 flex items-center justify-center">
              <FeatherCheck className="w-8 h-8 text-success-600" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              All purchases completed!
            </p>
            <p className="text-body font-body text-subtext-color">
              Successfully purchased {completedPurchases.length} NFT{completedPurchases.length !== 1 ? 's' : ''}
            </p>
          </>
        )}

        {hasSuccesses && hasFailures && (
          <>
            <div className="w-16 h-16 rounded-full bg-warning-50 flex items-center justify-center">
              <FeatherAlertCircle className="w-8 h-8 text-warning-600" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              Sweep partially completed
            </p>
            <p className="text-body font-body text-subtext-color">
              {completedPurchases.length} purchased, {failedPurchases.length} failed
            </p>
          </>
        )}

        {!hasSuccesses && hasFailures && (
          <>
            <div className="w-16 h-16 rounded-full bg-error-50 flex items-center justify-center">
              <FeatherX className="w-8 h-8 text-error-600" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              Sweep failed
            </p>
            <p className="text-body font-body text-subtext-color">
              No NFTs were purchased
            </p>
          </>
        )}

        {!hasSuccesses && !hasFailures && (
          <>
            <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center">
              <FeatherX className="w-8 h-8 text-subtext-color" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              Sweep cancelled
            </p>
            <p className="text-body font-body text-subtext-color">
              No NFTs were purchased
            </p>
          </>
        )}
      </div>

      {/* Failed list */}
      {hasFailures && (
        <div className="flex flex-col gap-2">
          <p className="text-body-bold font-body-bold text-error-600">
            Failed purchases:
          </p>
          <div className="max-h-[150px] overflow-y-auto border border-solid border-error-200 rounded-md bg-error-50">
            {failedPurchases.map((failure, index) => (
              <div
                key={failure.tokenId}
                className={`p-2 ${index !== failedPurchases.length - 1 ? 'border-b border-solid border-error-200' : ''}`}
              >
                <p className="text-body-bold font-body-bold text-error-700 truncate">
                  {failure.name}
                </p>
                <p className="text-caption font-caption text-error-600">
                  {failure.error}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Done button */}
      <Button
        variant="brand-primary"
        onClick={onComplete}
        className="w-full"
      >
        Done
      </Button>
    </div>
  );
}
